import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  Loader2, Eye, CheckCircle2, Clock, AlertCircle, XCircle, Cloud,
  ChevronLeft, ChevronRight, RefreshCw, Key, Trash2, AlertTriangle,
  HardDrive, Users, Zap, Shield, ExternalLink, Copy
} from 'lucide-react';
import { toast } from 'sonner';

export const AdminClouds = () => {
  const { token, hasPermission } = useAdminAuth();
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const limit = 20;

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [instanceToDelete, setInstanceToDelete] = useState(null);
  const [deleteFromScapi, setDeleteFromScapi] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [confirmDeleteText, setConfirmDeleteText] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const fetchInstances = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: (page * limit).toString()
      });
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await fetch(`${backendUrl}/api/admin/clouds?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInstances(data.instances);
        setTotal(data.total);
      }
    } catch (error) {
      console.error('Clouds error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInstances();
  }, [token, backendUrl, page, statusFilter]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 0) {
        fetchInstances();
      } else {
        setPage(0);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const getStatusBadge = (status) => {
    const configs = {
      'ready': { class: 'bg-green-500/20 text-green-400', icon: CheckCircle2, label: 'Aktiv' },
      'pending': { class: 'bg-yellow-500/20 text-yellow-400', icon: Clock, label: 'Ausstehend' },
      'deploying': { class: 'bg-blue-500/20 text-blue-400', icon: Cloud, label: 'Deploying' },
      'configuring': { class: 'bg-purple-500/20 text-purple-400', icon: RefreshCw, label: 'Konfiguriert' },
      'failed': { class: 'bg-red-500/20 text-red-400', icon: AlertCircle, label: 'Fehler' },
      'cancelled': { class: 'bg-slate-500/20 text-slate-400', icon: XCircle, label: 'Storniert' },
    };
    return configs[status] || configs['pending'];
  };

  const handleRestartProvisioning = async (instanceId) => {
    if (!confirm('Provisioning wirklich neu starten?')) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/clouds/${instanceId}/restart-provisioning`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Provisioning neu gestartet');
        fetchInstances();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler beim Neustart');
    }
  };

  const handleResetPassword = async (instanceId) => {
    if (!confirm('Admin-Passwort wirklich zurücksetzen?')) return;
    
    try {
      const response = await fetch(`${backendUrl}/api/admin/clouds/${instanceId}/reset-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`Neues Passwort: ${data.admin_password}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler');
      }
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    }
  };

  const openDeleteDialog = (instance) => {
    setInstanceToDelete(instance);
    setDeleteFromScapi(false);
    setDeleteReason('');
    setConfirmDeleteText('');
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!instanceToDelete) return;
    if (confirmDeleteText !== 'LÖSCHEN') {
      toast.error('Bitte "LÖSCHEN" eingeben zur Bestätigung');
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(`${backendUrl}/api/admin/clouds/${instanceToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          delete_from_scapi: deleteFromScapi,
          reason: deleteReason || undefined
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Cloud erfolgreich gelöscht');
        if (data.errors && data.errors.length > 0) {
          data.errors.forEach(err => toast.warning(err));
        }
        setDeleteDialogOpen(false);
        fetchInstances();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Kopiert!');
  };

  const totalPages = Math.ceil(total / limit);
  const canEdit = hasPermission('clouds:edit');

  return (
    <div className="space-y-6" data-testid="admin-clouds">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Clouds</h1>
          <p className="text-slate-400 mt-1">{total} Instanzen insgesamt</p>
        </div>
        <Button variant="outline" onClick={fetchInstances} disabled={loading} className="border-slate-600 text-slate-300">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <Input
              placeholder="Suchen (Domain, E-Mail, Firma)..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-64 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              data-testid="cloud-search-input"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 bg-slate-700 border-slate-600 text-white" data-testid="cloud-status-filter">
                <SelectValue placeholder="Status filtern" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-slate-200 [&_*[role=option]]:text-slate-200 [&_*[role=option]]:focus:bg-slate-700 [&_*[role=option]]:focus:text-white">
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="ready">Aktiv</SelectItem>
                <SelectItem value="pending">Ausstehend</SelectItem>
                <SelectItem value="deploying">Deploying</SelectItem>
                <SelectItem value="configuring">Konfiguriert</SelectItem>
                <SelectItem value="failed">Fehler</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Instances Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : instances.length === 0 ? (
            <p className="text-slate-500 text-center py-20">Keine Instanzen gefunden</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Domain</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Kunde</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Plan</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">SCAPI</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {instances.map((instance) => {
                    const statusConfig = getStatusBadge(instance.status);
                    const StatusIcon = statusConfig.icon;
                    return (
                      <tr key={instance.id} className="border-b border-slate-700/50 hover:bg-slate-700/30" data-testid={`cloud-row-${instance.id}`}>
                        <td className="py-4 px-4">
                          <div>
                            <span className="text-white font-medium">
                              {instance.plan?.custom_domain || 'N/A'}
                            </span>
                            {instance.url && (
                              <a href={instance.url} target="_blank" rel="noopener noreferrer" 
                                 className="flex items-center gap-1 text-blue-400 text-sm hover:underline">
                                {instance.url}
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div>
                            <span className="text-white">{instance.customer_email || 'N/A'}</span>
                            {instance.customer_company && (
                              <span className="block text-slate-400 text-sm">{instance.customer_company}</span>
                            )}
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex flex-col gap-1">
                            <span className="text-slate-300 flex items-center gap-1">
                              <HardDrive className="h-3 w-3 text-slate-500" />
                              {instance.plan?.storage_gb || 0} GB
                            </span>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              {instance.plan?.collabora_users > 0 && (
                                <span className="flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {instance.plan.collabora_users}
                                </span>
                              )}
                              {instance.plan?.hp_backend && (
                                <span className="flex items-center gap-1 text-amber-500">
                                  <Zap className="h-3 w-3" />
                                </span>
                              )}
                              {instance.plan?.antivirus && (
                                <span className="flex items-center gap-1 text-green-500">
                                  <Shield className="h-3 w-3" />
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <Badge className={statusConfig.class}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                          {instance.progress > 0 && instance.progress < 100 && (
                            <span className="block text-xs text-slate-400 mt-1">
                              {instance.progress}%
                            </span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          {instance.scapi_instance_id ? (
                            <button 
                              onClick={() => copyToClipboard(instance.scapi_instance_id)}
                              className="text-xs font-mono text-slate-500 hover:text-slate-300 flex items-center gap-1"
                              title={instance.scapi_instance_id}
                            >
                              {instance.scapi_instance_id.substring(0, 8)}...
                              <Copy className="h-3 w-3" />
                            </button>
                          ) : (
                            <span className="text-xs text-slate-600">-</span>
                          )}
                        </td>
                        <td className="py-4 px-4 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button asChild variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300">
                              <Link to={`/admin/clouds/${instance.id}`}>
                                <Eye className="h-4 w-4" />
                              </Link>
                            </Button>
                            {canEdit && instance.status === 'failed' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRestartProvisioning(instance.id)}
                                className="text-yellow-400 hover:text-yellow-300"
                                title="Provisioning neu starten"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </Button>
                            )}
                            {canEdit && instance.status === 'ready' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleResetPassword(instance.id)}
                                className="text-orange-400 hover:text-orange-300"
                                title="Passwort zurücksetzen"
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                            )}
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openDeleteDialog(instance)}
                                className="text-red-400 hover:text-red-300"
                                title="Cloud löschen"
                                data-testid={`delete-cloud-${instance.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-700">
              <span className="text-slate-400 text-sm">
                Seite {page + 1} von {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-lg bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle className="h-5 w-5" />
              Cloud löschen
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Diese Aktion kann nicht rückgängig gemacht werden.
            </DialogDescription>
          </DialogHeader>

          {instanceToDelete && (
            <div className="space-y-4 py-4">
              {/* Instance Info */}
              <div className="bg-slate-700/50 rounded-lg p-4 space-y-2">
                <p className="font-medium text-white">
                  {instanceToDelete.plan?.custom_domain || 'Unbenannte Cloud'}
                </p>
                <p className="text-sm text-slate-400">
                  Kunde: {instanceToDelete.customer_email || 'N/A'}
                </p>
                {instanceToDelete.scapi_instance_id && (
                  <p className="text-xs font-mono text-slate-500">
                    SCAPI: {instanceToDelete.scapi_instance_id}
                  </p>
                )}
              </div>

              {/* What will be deleted */}
              <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
                <p className="text-sm font-medium text-red-400 mb-2">Folgendes wird gelöscht:</p>
                <ul className="text-sm text-slate-400 space-y-1 list-disc list-inside">
                  <li>Instanz-Datensatz</li>
                  <li>Subscription/Vertrag</li>
                  <li>Plan-Konfiguration</li>
                  <li>Bestellungen werden als storniert markiert</li>
                  <li>Rechnungen werden als storniert markiert</li>
                </ul>
                <p className="text-sm text-slate-500 mt-2 pt-2 border-t border-red-700/30">
                  <strong className="text-slate-400">Nicht gelöscht:</strong> Kundenkonto, Zahlungsdaten
                </p>
              </div>

              {/* SCAPI Delete Option */}
              {instanceToDelete.scapi_instance_id && (
                <div className="flex items-start gap-3 p-4 bg-amber-900/20 border border-amber-700/50 rounded-lg">
                  <Checkbox
                    id="delete-scapi"
                    checked={deleteFromScapi}
                    onCheckedChange={setDeleteFromScapi}
                    className="mt-0.5 border-slate-500"
                  />
                  <div className="flex-1">
                    <label htmlFor="delete-scapi" className="text-sm font-medium text-white cursor-pointer">
                      Auch bei SCAPI löschen
                    </label>
                    <p className="text-xs text-slate-400 mt-1">
                      Löscht die Nextcloud-Instanz auch beim Hosting-Provider. 
                      <span className="text-amber-400 font-medium"> Alle Daten des Kunden gehen verloren!</span>
                    </p>
                  </div>
                </div>
              )}

              {/* Reason */}
              <div>
                <label className="text-sm font-medium text-white mb-2 block">
                  Grund für Löschung (optional)
                </label>
                <Textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="z.B. Kunde hat gekündigt, Test-Instanz, etc."
                  className="min-h-20 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>

              {/* Confirmation */}
              <div>
                <label className="text-sm font-medium text-white mb-2 block">
                  Zur Bestätigung "LÖSCHEN" eingeben:
                </label>
                <Input
                  value={confirmDeleteText}
                  onChange={(e) => setConfirmDeleteText(e.target.value.toUpperCase())}
                  placeholder="LÖSCHEN"
                  className="font-mono bg-slate-700 border-slate-600 text-white"
                  data-testid="confirm-delete-input"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting} className="border-slate-600 text-slate-300">
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting || confirmDeleteText !== 'LÖSCHEN'}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Lösche...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Endgültig löschen
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
